import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, Loader2, Lock, ShieldCheck, Smartphone } from "lucide-react";
import { api, errMsg, ghs } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ProductImage } from "../../components/site/ProductCard";
import { EmptyState } from "../../components/site/Bits";
import { FullLoader } from "../../components/site/Guards";

export default function Checkout() {
  const { type, id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [mode, setMode] = useState("");
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const isProduct = type === "product";

  useEffect(() => {
    const url = isProduct ? `/products/${id}` : `/predictions/packages/${id}`;
    api.get(url).then((r) => setItem(isProduct ? r.data : r.data.package)).catch(() => setMissing(true));
    api.get("/payments/config").then((r) => setMode(r.data.mode)).catch(() => {});
  }, [id, isProduct]);

  if (missing || !["product", "prediction_package"].includes(type))
    return <EmptyState title="Item not found" text="This item is no longer available." action={<Link to="/dashboard" className="btn-ghost">Back to dashboard</Link>} />;
  if (!item) return <FullLoader />;

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/initiate", { item_type: type, item_id: id });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(errMsg(e));
      if (e.response?.status === 409) nav(isProduct ? "/purchases" : "/predictions");
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="checkout-back-btn"><ArrowLeft size={16} /> Back</button>
      <h1 className="mt-6 text-3xl font-bold text-white">Checkout</h1>
      {mode === "mock" && (
        <p className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="checkout-test-mode-banner">
          Test mode — theteller credentials are not configured yet, so payments are simulated. No money will be charged.
        </p>
      )}
      <div className="mt-8 grid gap-6 lg:grid-cols-5">
        <div className="card-rx p-6 lg:col-span-3">
          <p className="eyebrow">Order summary</p>
          <div className="mt-5 flex gap-4">
            <div className="h-20 w-24 shrink-0 overflow-hidden rounded-xl"><ProductImage path={item.image_path} alt={item.name} /></div>
            <div><h2 className="text-lg font-semibold text-white" data-testid="checkout-item-name">{item.name}</h2><p className="mt-1 text-sm text-slate-400">{isProduct ? "Digital product" : `Prediction package · ${item.duration_days} day(s) access`}</p></div>
          </div>
          <div className="mt-6 space-y-3 border-t border-white/[0.06] pt-5 text-sm">
            <div className="flex justify-between text-slate-400"><span>Customer</span><span className="text-slate-200">{user?.full_name} · {user?.phone}</span></div>
            <div className="flex justify-between text-slate-400"><span>Currency</span><span className="text-slate-200">Ghana Cedi (GHS)</span></div>
            <div className="flex justify-between border-t border-white/[0.06] pt-4 text-base font-semibold text-white"><span>Total</span><span className="font-mono-rx text-amber-400" data-testid="checkout-total">{ghs(item.price_minor)}</span></div>
          </div>
        </div>
        <div className="card-rx flex flex-col p-6 lg:col-span-2">
          <p className="eyebrow">Pay with theteller</p>
          <ul className="mt-5 space-y-3 text-sm text-slate-300">
            <li className="flex items-center gap-3"><Smartphone size={18} className="text-emerald-400" /> MTN MoMo · Telecel Cash · AT Money</li>
            <li className="flex items-center gap-3"><CreditCard size={18} className="text-emerald-400" /> Visa · Mastercard</li>
            <li className="flex items-center gap-3"><ShieldCheck size={18} className="text-emerald-400" /> Verified server-side before access</li>
          </ul>
          <button onClick={pay} disabled={busy} className="btn-primary mt-8 w-full" data-testid="checkout-pay-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Pay {ghs(item.price_minor)}
          </button>
          <p className="mt-3 text-center text-xs text-slate-500">You'll be redirected to theteller's secure checkout.</p>
        </div>
      </div>
    </div>
  );
}
